<template>
    <div class="filter-steps--container">
        <team-region-member-filter-options
            :team-region-id="teamRegionId"
            :team-member-public-userid="teamMemberFilterValue"
            @changed="handleTeamRegionMemberChanged"
            :strict-team-region-filter="false"
            :clearable="false"
            :show-carriers="false"
        />
        <md-field>
            <vue-select
                class="select"
                label="label"
                :options="sortedFieldGroupOptions"
                placeholder="Document Type"
                v-model="selectedDocGroup"
                :searchable="$root.isDesktop"
            >
                <template v-slot:option="option">
                    <span>{{ option.label }}</span>
                </template>
            </vue-select>
        </md-field>
        <md-field>
            <md-datepicker
                data-testid="start-date"
                class="filter-steps--choices filter-step-date start-date"
                v-model="fromDate"
                md-immediately
                :md-debounce="100"
            ></md-datepicker>
        </md-field>
        <md-field>
            <md-datepicker
                data-testid="end-date"
                class="filter-steps--choices filter-step-date end-date"
                v-model="toDate"
                md-immediately
                :md-debounce="100"
            ></md-datepicker>
        </md-field>
    </div>
</template>
<script>
import { GeneralMixin } from '@/mixins';
import { DATE_TYPES } from '@/utils/constants';
import _ from 'lodash';
import { TeamRegionMemberFilterOptions } from '@/components';
import moment from 'moment';
import { fieldGroupOptions } from '@/utils/customFields';

export default {
    name: 'TeamMemberDocumentsReportFilter',
    components: { TeamRegionMemberFilterOptions },
    props: {
        teamRegionId: { type: Number, default: null },
        teamMembers: {
            type: Function,
            default: () => []
        },
        filterValue: {
            type: Object,
            default: null
        }
    },
    mixins: [GeneralMixin],
    data() {
        return {
            teamMemberFilterValue: '',
            teamRegionFilterValue: '',
            selectedTeamRegionId: null,
            fromDate: null,
            toDate: null,
            selectedDocGroup: null
        };
    },
    computed: {
        sortedFieldGroupOptions() {
            const sorted = fieldGroupOptions.sort((a, b) => a.label.localeCompare(b.label));

            return sorted;
        }
    },
    mounted() {
        if (!this.$root.isDesktop) {
            if (this.$refs.mdDatePicker) 
                this.$refs.mdDatePicker.$refs.input.$el.setAttribute('readonly', true);
        }
        this.handleFilterSetValue();
    },
    watch: {
        filterValue() {
            this.handleFilterSetValue();
        },
        fromDate(newVal, oldVal) {
            if (
                moment(newVal).format(DATE_TYPES.internationalDate) ===
                moment(oldVal).format(DATE_TYPES.internationalDate)
            ) {
                return;
            }

            if (
                this.fromDate &&
                this.toDate &&
                moment(newVal).format(DATE_TYPES.internationalDate) >
                    moment(this.toDate).format(DATE_TYPES.internationalDate)
            ) {
                this.$notify({
                    message: 'From date can not be later than the to date.',
                    type: 'danger'
                });
                return;
            }

            this.handleFilter();
        },
        toDate(newVal, oldVal) {
            if (
                moment(newVal).format(DATE_TYPES.internationalDate) ===
                moment(oldVal).format(DATE_TYPES.internationalDate)
            ) {
                return;
            }

            if (
                this.fromDate &&
                this.toDate &&
                moment(newVal).format(DATE_TYPES.internationalDate) <
                    moment(this.fromDate).format(DATE_TYPES.internationalDate)
            ) {
                this.$notify({
                    message: 'To date can not be earlier than the from date.',
                    type: 'danger'
                });
                return;
            }

            this.handleFilter();
        },
        selectedDocGroup(newVal) {
            this.handleFilter();
        }
    },
    methods: {
        // eslint-disable-next-line func-names
        handleFilter: _.debounce(function() {
            this.handleFilteredValue();
        }, 700),
        clearFilters() {
            this.teamMemberFilterValue = '';
            this.selectedTeamRegionId = null;
            this.fromDate = null;
            this.toDate = null;
            this.selectedDocGroup = null;
        },
        handleFilteredValue() {
            // use nextTick to ensure that date values have already been changed.
            // this is because the @selected method of md-datepicker is asynchronous.
            this.$nextTick(() => {
                const { teamMemberFilterValue } = this;

                let fromDate = null;
                if (this.fromDate) {
                    fromDate = this.$options.filters.dateFormat(this.fromDate, this.DATE_TYPES.internationalDate);
                }

                let toDate = null;
                if (this.toDate) {
                    toDate = this.$options.filters.dateFormat(this.toDate, this.DATE_TYPES.internationalDate);
                }

                const eventArgs = {
                    teamMemberPublicUserId: teamMemberFilterValue,
                    teamRegionId: this.selectedTeamRegionId,
                    fromDate,
                    toDate,
                    documentGroup: this.selectedDocGroup ? this.selectedDocGroup.value : null
                };
                this.$emit('changed', eventArgs);
            });
        },

        handleTeamRegionMemberChanged(val) {
            this.teamMemberFilterValue = val.type === 'Member' ? val.publicUserId : null;
            this.selectedTeamRegionId = val.teamRegionId;
            this.handleFilter();
        },
        handleFilterSetValue() {
            if (this.filterValue.fromDate) {
                this.fromDate = this.filterValue.fromDate;
            }

            if (this.filterValue.toDate) {
                this.toDate = this.filterValue.toDate;
            }

            if (this.filterValue.teamMemberPublicUserId) {
                this.teamMemberFilterValue = this.filterValue.teamMemberPublicUserId;
            }

            if (this.filterValue.teamRegionId) {
                this.selectedTeamRegionId = Number(this.filterValue.teamRegionId);
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.filter-steps--container {
    ::v-deep .md-field {
        display: inline-block;
        width: 200px;
        min-height: 48px;
        margin-right: 20px;
        margin-top: 0;
        vertical-align: top;
    }
    .filter-step-date {
        min-width: 250px;
        margin-right: 0;
        min-height: initial;
        position: absolute;
        z-index: 1;
    }

    .filter-status {
        min-width: 150px;
        margin-right: 0;
        min-height: initial;
        position: absolute;
    }

    .filter-button--container {
        display: inline-block;
    }
    ::v-deep .md-datepicker:after,
    ::v-deep .md-datepicker:before {
        bottom: 0;
        height: 0;
    }

    ::v-deep .md-datepicker:before,
    ::v-deep .md-datepicker:after {
        bottom: 30px;
        color: #aaaaaa;
        font-weight: 400;
        font-size: 14px;
        transform: none;
        z-index: 1;
    }

    .start-date:before,
    .start-date:after {
        content: 'Start Date';
    }
    .end-date:before,
    .end-date:after {
        content: 'End Date';
    }

    ::v-deep .md-has-value:before,
    ::v-deep .md-has-value:after {
        bottom: 0;
        height: 0;
        content: '';
    }

    ::v-deep .md-datepicker .md-input {
        position: absolute;
        z-index: 9;
        width: 150px;
    }

    ::v-deep .md-datepicker .md-input-action {
        z-index: 99;
        right: 52px;
    }
}
::v-deep .vs__search::placeholder {
    color: #aaaaaa;
    font-size: 14px;
}
::v-deep .vs--searchable .vs__dropdown-toggle {
    border: none;
    padding-bottom: 5px;
    margin-top: 4px;
    background-color: transparent;
    cursor: pointer;
}
::v-deep .vs--disabled .vs__clear,
::v-deep .vs--disabled .vs__dropdown-toggle,
::v-deep .vs--disabled .vs__open-indicator,
::v-deep .vs--disabled .vs__search,
::v-deep .vs--disabled .vs__selected {
    background-color: transparent;
}

::v-deep .vs__search {
    cursor: pointer;
}

.datepicker-noevent {
    ::v-deep .md-datepicker {
        input {
            pointer-events: none;
        }
    }
}

.time-picker .main-time-picker-container:after {
    content: 'Select time';
    color: #aaaaaa;
    font-weight: 400;
    font-size: 14px;
    transform: none;
    bottom: 17px;
    position: absolute;
}

::v-deep .md-has-value .main-time-picker-container:after {
    content: '';
}
.time-picker {
    width: 210px !important;
}

.time-picker.md-field:after {
    bottom: 10px !important;
    height: 1px !important;
}
.time-picker:last-child {
    margin-right: 0 !important;
}

.status-filter {
    width: 150px !important;
}

.time-picker ::v-deep .time-picker-container:nth-child(2) {
    border-bottom: 1px solid #d2d2d2;
}
</style>
